import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form } from '@remix-run/react'
import { useState } from 'react'
import { z } from 'zod'
import {
  emailSchema,
  firstNameSchema,
  phoneSchema,
  postalCodeSchema,
} from '#app/shared/lib/user-validation.js'
import { checkboxSchema } from '#app/shared/lib/zod-extensions.js'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { Field, PhoneField } from '#app/shared/ui/fields.js'
import { Button } from '#app/shared/ui/inputs/button/Button.js'
import { CheckboxTermsAndPrivacy } from '#app/shared/ui/inputs/CheckboxTermsAndPrivacy.js'
import { Tabs, TabsList, TabsTrigger } from '#app/shared/ui/navigation/Tabs.js'

export const ContactFormSchema = z.object({
  email: emailSchema,
  phoneNumber: phoneSchema,
  fullPhone: z.string(),
  name: firstNameSchema,
  postalCode: postalCodeSchema,
  message: z.string(),
  acceptTerms: checkboxSchema(),
})

export default function ContactForm() {
  const [isTermsAccepted, setIsTermsAccepted] = useState(false)
  const [selectedTab, setSelectedTab] = useState('Financiado')
  const [form, fields] = useForm({
    id: 'contact-form',
    constraint: getZodConstraint(ContactFormSchema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ContactFormSchema })
    },
    shouldRevalidate: 'onBlur',
  })

  return (
    <Form
      className={`
        w-full

        lg:max-w-[600px]

        xl:w-[900px] xl:min-w-[900px] xl:max-w-full xl:pl-[100px] xl:pt-[31px]
      `}
      {...getFormProps(form)}
      method="post"
    >
      <p
        className={`
          text-h5-bold text-neutral-900

          xl:text-h3
        `}
      >
        ¿Te queda alguna pregunta? Contáctanos.
      </p>

      <Spacer size="16px" />
      <p className="text-base-medium text-neutral-500">
        Estamos aquí para ayudarte a tomar la mejor decisión. ¡No dudes en
        consultarnos!
      </p>
      <Spacer size="28px" sizeMd="36px" />

      <input
        type="hidden"
        className="hidden"
        name="message"
        value={selectedTab}
      />
      <Tabs
        value={selectedTab}
        onValueChange={(value) => {
          setSelectedTab(value)
        }}
        className="xl:w-[700px]"
      >
        <p className="mb-3 text-subtitle1-bold text-neutral-900">
          ¿Estás interesado en financiarlo o prefieres pagar al contado?
        </p>
        <TabsList
          className={`
            flex !h-14 items-center gap-4 !rounded-[8px] !border
            !border-neutral-100 !bg-white px-1.5
          `}
        >
          <TabsTrigger
            className={`
              h-11 text-base-bold font-bold text-neutral-500

              data-[state=active]:rounded-base data-[state=active]:border
              data-[state=active]:border-neutral-100
              data-[state=active]:bg-neutral-25
              data-[state=active]:text-neutral-900

              hover:border-0 hover:text-neutral-500
            `}
            value="Financiado"
          >
            Financiado
          </TabsTrigger>
          <TabsTrigger
            className={`
              h-11 text-base-bold font-bold text-neutral-500

              data-[state=active]:rounded-base data-[state=active]:border
              data-[state=active]:border-neutral-100
              data-[state=active]:bg-neutral-25
              data-[state=active]:text-neutral-900

              hover:border-0 hover:text-neutral-500
            `}
            value="Al contado"
          >
            Al contado
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Spacer size="28px" />
      <div className="flex flex-col gap-7">
        <Field
          labelProps={{
            children: 'Nombre',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.name, { type: 'text' }),
            autoComplete: 'name',
            autoFocus: true,
            placeholder: 'Escribe aquí tu nombre',
            variant: 'outlined',
            className:
              'max-w-full xl:max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.name.errors}
        />

        <Field
          labelProps={{
            children: 'Código postal',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.postalCode, { type: 'text' }),
            autoComplete: 'postal code',
            placeholder: 'Escribe aquí tu código postal',
            variant: 'outlined',
            className:
              'max-w-full xl:max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.postalCode.errors}
        />

        <Field
          labelProps={{
            children: 'Email',
            className: 'text-subtitle1-bold text-neutral-900',
          }}
          inputProps={{
            ...getInputProps(fields.email, { type: 'text' }),
            autoComplete: 'email',
            placeholder: 'Escribe aquí tu email',
            variant: 'outlined',
            className:
              'max-w-full xl:max-w-[684px] !mt-3 !h-14 text-base-medium text-neutral-500 border-neutral-100',
            size: '64px',
          }}
          errors={fields.email.errors}
        />
        <div className="xl:max-w-[684px]">
          <PhoneField
            labelProps={{
              htmlFor: fields.phoneNumber.id,
              children: 'Teléfono',
            }}
            inputProps={{
              ...getInputProps(fields.phoneNumber, { type: 'number' }),
              autoComplete: 'phone',
              placeholder: 'Escribe aquí tu teléfono',
              className: 'mt-2',
            }}
            fullPhone={fields.fullPhone.name}
            errors={fields.phoneNumber.errors}
          />
        </div>
      </div>
      <Spacer size="24px" />

      <CheckboxTermsAndPrivacy
        isTermsAccepted={isTermsAccepted}
        setIsTermsAccepted={setIsTermsAccepted}
      />
      <Spacer size="28px" />

      <Button
        variant="primary"
        className={`
          px-6 py-5 text-base-bold text-white

          disabled:opacity-20
        `}
        disabled={!isTermsAccepted}
        type="submit"
      >
        Enviar
      </Button>
    </Form>
  )
}
